import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomCheckbox,
  CustomDropdown,
  CustomRadioGroup,
  CustomTextInput,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const ExamRulesCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";

  const { translations } = useSelector((state) => state.language);

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [examNameOptions, setExamNameOptions] = useState([]);
  const [formData, setFormData] = useState({
    examConfigId: "",
    passRules: ["theory"],
    status: "1",
  });
  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Fetch exam names on mount
  useEffect(() => {
    if (school_id && academic_year_id) {
      fetchExamNames();
    }
  }, [school_id, academic_year_id]);

  // Load edit data
  useEffect(() => {
    if (editId) {
      fetchExamRulesDetails();
    }
  }, [editId]);

  // Fetch exam names
  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };

      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );

      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = examData.map((item) => ({
          value: item.id,
          label: item.exam_type || item.exam_name,
        }));
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
    }
  }, [school_id, academic_year_id]);

  // Fetch exam rules details for edit mode
  const fetchExamRulesDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/exam/examRules/view/${editId}`,
      );

      if (response?.data?.status) {
        const data = response.data.data[0] || response.data.data;
        const passRulesArray = [];
        if (data.theory === 1 || data.theory === true)
          passRulesArray.push("theory");
        if (data.practical === 1 || data.practical === true)
          passRulesArray.push("practical");

        setFormData({
          examConfigId: data.campus_exam_config_id || "",
          passRules: passRulesArray,
          status: data.is_active ? "1" : "0",
        });
      }
    } catch (error) {
      console.error("Error fetching exam rules details:", error);
      alert(
        translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [editId, translations.labels?.fetchFailed]);

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCheckboxChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      passRules: value,
    }));

    if (errors.passRules) {
      setErrors((prev) => ({
        ...prev,
        passRules: "",
      }));
    }
  };

  const handleRadioChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const examConfigValue = getExamConfigIdValue();
    if (!examConfigValue) {
      newErrors.examConfigId =
        translations.labels?.examNameRequired || "Exam Name is required";
    }

    if (formData.passRules.length === 0) {
      newErrors.passRules =
        translations.labels?.passRulesRequired ||
        "At least one pass rule must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get exam config id value (handles both object and primitive)
  const getExamConfigIdValue = () => {
    if (
      typeof formData.examConfigId === "object" &&
      formData.examConfigId !== null
    ) {
      return formData.examConfigId.value;
    }
    return formData.examConfigId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      let response;
      const theoryValue = formData.passRules.includes("theory") ? 1 : 0;
      const practicalValue = formData.passRules.includes("practical") ? 1 : 0;
      const examConfigIdValue = getExamConfigIdValue();

      if (editId) {
        // Update payload
        const payload = {
          id: parseInt(editId),
          client_id: client_id,
          school_id: parseInt(school_id),
          academic_id: parseInt(academic_year_id),
          exam_config_id: parseInt(examConfigIdValue),
          theory: theoryValue,
          practical: practicalValue,
          isActive: parseInt(formData.status),
          updated_by: parseInt(user_id),
        };
        response = await AxiosInstance.put("/exam/examRules/update", payload);
      } else {
        // Create payload
        const payload = {
          client_id: client_id,
          school_id: parseInt(school_id),
          academic_id: parseInt(academic_year_id),
          exam_config_id: parseInt(examConfigIdValue),
          theory: theoryValue,
          practical: practicalValue,
          isActive: parseInt(formData.status),
          created_by: parseInt(user_id),
        };
        response = await AxiosInstance.post("/exam/examRules/create", payload);
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.updateSuccess ||
              "Exam rules updated successfully!"
            : translations.labels?.createSuccess ||
              "Exam rules created successfully!",
          redirectPath: "/user/examRulesList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.saveFailed ||
          "Failed to save exam rules. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) return;

    if (
      !window.confirm(
        translations.labels?.confirmDelete ||
          "Are you sure you want to delete this exam rule?",
      )
    ) {
      return;
    }

    setDeleting(true);

    try {
      const payload = {
        id: parseInt(editId),
        deleted_by: parseInt(user_id),
      };

      const response = await AxiosInstance.delete("/exam/examRules/delete", {
        data: payload,
      });

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels?.deleteSuccess || "Deleted successfully!",
          redirectPath: "/user/examRulesList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Failed to delete!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.deleteFailed ||
          "Failed to delete. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/examRulesList");
  };

  // Dynamic checkbox options with individual disable control
  const passRulesOptions = [
    {
      value: "theory",
      label: translations.labels?.theory || "Theory",
      disabled: false,
    },
    {
      value: "practical",
      label: translations.labels?.practical || "Practical",
      disabled: false,
    },
  ];

  // Dynamic radio options with individual disable control
  const statusOptions = [
    {
      value: "1",
      label: translations.labels?.active || "Active",
      disabled: false,
    },
    {
      value: "0",
      label: translations.labels?.inactive || "Inactive",
      disabled: false,
    },
  ];
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  if (loading) {
    return (
      <>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaCalendarAlt />
            </div>
            <h1 className={styles.headerTitle}>
              {translations.labels?.examRules || "Exam Rules"}
            </h1>
          </div>
          <div className={styles.loadingContainer}>
            <p>{translations.labels?.loading || "Loading..."}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.examRules || "Exam Rules"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year (Read-only) */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
              />
            </div>

            {/* Exam Name */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.examName || "Exam Name"}
                name="examConfigId"
                value={formData.examConfigId}
                onChange={(value) =>
                  handleDropdownChange("examConfigId", value)
                }
                options={examNameOptions}
                placeholder={
                  translations.labels?.selectExamName || "Select Exam Name"
                }
                required
                error={errors.examConfigId}
              />
            </div>
          </div>

          {/* Exam Pass Rules - Using CustomCheckbox with options */}
          <div className="row">
            <div className="col-lg-12 px-2">
              <CustomCheckbox
                label={translations.labels?.examPassRules || "Exam Pass Rules"}
                name="passRules"
                value={formData.passRules}
                onChange={handleCheckboxChange}
                options={passRulesOptions}
                required
                error={errors.passRules}
                inline
              />
            </div>
          </div>

          {/* Status - Using CustomRadioGroup with options */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomRadioGroup
                label={translations.labels?.status || "Status"}
                name="status"
                value={formData.status}
                onChange={(value) => handleRadioChange("status", value)}
                options={statusOptions}
                required
              />
            </div>
          </div>

          {/* Note */}
          <div className={styles.noteContainer}>
            <p className={styles.noteText}>
              <span className={styles.noteLabel}>
                {translations.labels?.note || "Note"}:
              </span>{" "}
              {translations.labels?.examRulesNote ||
                "Only Students who meet the passing criteria will be eligible for ranking."}
            </p>
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {editId
                ? translations.labels?.update || "UPDATE"
                : translations.labels?.submit || "SUBMIT"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            {/* Delete Button - Only show in edit mode */}
            {editId && (
              <CustomButton
                type="button"
                variant="contained"
                className={styles.deleteButton}
                fullWidth={false}
                onClick={handleDelete}
                loading={deleting}
                loadingText={translations.labels?.deleting || "Deleting..."}
              >
                {translations.labels?.delete || "DELETE"}
              </CustomButton>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default ExamRulesCreate;
